import { fetchPlanEntitlements } from "./subscription-service";
import { applyEntitlements } from "./entitlements";

/*
 * The SaaS feature catalogue (SAAS-REVAMP/DESIGN.md §2.4) as served with the tenant's entitlements
 * by POST /api/client/saas/entitlements: { entitlements, catalog: { features, groups, limits } }.
 * Feature, group and limit labels come from here in both languages; the key itself is only the
 * last-resort label.
 */
let cached = null;
let pending = null;

/**
 * Loads (once, unless `force`) the tenant's entitlements + catalogue. A successful answer also
 * refreshes the entitlements the app runs on. Resolves the service result
 * `{ success, status, data, error }`; never rejects.
 */
export function loadPlanDetails({ force = false } = {}) {
  if (cached && !force) return Promise.resolve({ success: true, status: 200, data: cached, error: null });
  if (pending) return pending;
  pending = fetchPlanEntitlements().then(result => {
    pending = null;
    if (result.success && result.data && typeof result.data === "object") {
      cached = result.data;
      if (Object.prototype.hasOwnProperty.call(result.data, "entitlements")) applyEntitlements(result.data);
    }
    return result;
  });
  return pending;
}

/** `{ en, ar }` (or a plain string) in `locale`, falling back to the other language. */
export function localizedText(value, locale) {
  if (!value) return "";
  if (typeof value === "string") return value;
  const primary = locale === "ar" ? "ar" : "en";
  const secondary = primary === "ar" ? "en" : "ar";
  return value[primary] || value[secondary] || "";
}

/** "dietitian_clinic" -> "Dietitian clinic" — only when the catalogue has no label. */
export function humanizeKey(key) {
  const text = String(key || "").replace(/_/g, " ").trim();
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : "";
}

function findByKey(list, key) {
  if (!Array.isArray(list)) return null;
  for (let i = 0; i < list.length; i++) {
    if (list[i] && list[i].key === key) return list[i];
  }
  return null;
}

export function featureDef(catalog, key) {
  return findByKey(catalog && catalog.features, key);
}

export function featureLabel(catalog, key, locale) {
  const def = featureDef(catalog, key);
  return (def && localizedText(def.label, locale)) || humanizeKey(key);
}

export function limitDef(catalog, key) {
  return findByKey(catalog && catalog.limits, key);
}

export function limitLabel(catalog, key, locale) {
  const def = limitDef(catalog, key);
  return (def && localizedText(def.label, locale)) || humanizeKey(String(key || "").replace(/^max_/, ""));
}

export function groupLabel(catalog, key, locale) {
  const def = findByKey(catalog && catalog.groups, key);
  return (def && localizedText(def.label, locale)) || humanizeKey(key);
}
